import { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";

import { OrgaConfiguration } from "@/models";
import { OrgaConfigurationRepository } from "@/services/firebase/orga-configuration-repository";
import { getOrganizationLogoUrl } from "@/shared/utils/organization-logo";

/**
 * Hook personnalisé pour récupérer la configuration d'organisation
 * Utilise l'organizationId de l'URL pour récupérer les informations de l'organisation
 * Se recharge automatiquement quand l'ID de l'organisation change dans l'URL
 */
interface UseOrgaConfigOptions {
  once?: boolean;
  initialConfiguration?: OrgaConfiguration | null;
}

/**
 * Hook personnalisé pour récupérer la configuration d'organisation
 * Utilise l'organizationId de l'URL pour récupérer les informations de l'organisation
 * Par défaut, utilise un listener temps réel (désactivable via options.once)
 */
export const useOrgaConfiguration = (
  organizationIdOverride?: string,
  options: UseOrgaConfigOptions = {}
) => {
  const { idorga: paramId } = useParams<{ idorga: string }>();
  const idorga = organizationIdOverride || paramId;

  const [configuration, setConfiguration] = useState<OrgaConfiguration | null>(options.initialConfiguration || null);
  const [loading, setLoading] = useState(!options.initialConfiguration);
  const [error, setError] = useState<string | null>(null);
  const lastConfigRef = useRef<string | null>(null);

  useEffect(() => {
    // Mode order journey : config injectée par OrganizationGuard (doit suivre les mises à jour)
    if (options.once && options.initialConfiguration) {
      setConfiguration(options.initialConfiguration);
      setLoading(false);
      setError(null);
      return;
    }

    if (!idorga) {
      setLoading(false);
      setError("Aucune organisation sélectionnée");
      return;
    }

    // Si on a une config initiale mais qu'on veut un listener, on démarre le loading quand même
    if (!options.initialConfiguration) {
      setLoading(true);
    }
    setError(null);
    lastConfigRef.current = null;

    if (options.once) {
      // MODE LECTURE UNIQUE (Optimisé pour l'order journey)
      // Si on a déjà la config initiale, on ne fait rien
      if (options.initialConfiguration) {
        setLoading(false);
        return;
      }

      console.log('📖 useOrgaConfiguration: Lecture UNIQUE pour', idorga);
      OrgaConfigurationRepository.getByOrganizationId(idorga)
        .then((config) => {
          setConfiguration(config);
          setLoading(false);
          if (!config) {
            setError(`Configuration non trouvée pour l'organisation: ${idorga}`);
          }
        })
        .catch((err) => {
          console.error('❌ Erreur lecture unique orga:', err);
          setError("Erreur lors du chargement de la configuration");
          setLoading(false);
        });
      return () => { }; // Rien à nettoyer
    }

    // MODE LISTENER (Pour l'admin / édition)
    console.log('🔄 useOrgaConfiguration: Création listener pour', idorga);

    const unsubscribe = OrgaConfigurationRepository.listenToOrganizationConfig(
      idorga,
      (config) => {
        const configString = JSON.stringify(config);
        if (lastConfigRef.current === configString) {
          return;
        }
        lastConfigRef.current = configString;

        console.log('✅ useOrgaConfiguration (Listener): Configuration mise à jour pour', idorga);
        setConfiguration(config);
        setLoading(false);

        if (!config) {
          setError(`Configuration non trouvée pour l'organisation: ${idorga}`);
        }
      },
    );

    return () => {
      console.log('🧹 useOrgaConfiguration: Nettoyage du listener pour', idorga);
      unsubscribe();
    };
  }, [idorga, options.once, options.initialConfiguration]);

  // Fonction de rafraîchissement manuel
  // silent: si true, ne met pas loading=true (évite l'éclipse de l'interface)
  const refreshConfiguration = (opts?: { silent?: boolean }) => {
    if (!idorga) return;
    const silent = opts?.silent ?? false;
    if (!silent) setLoading(true);
    OrgaConfigurationRepository.getByOrganizationId(idorga)
      .then((config) => {
        setConfiguration(config);
        setLoading(false);
      })
      .catch(() => {
        setLoading(false);
      });
  };

  return {
    configuration,
    loading,
    error,
    refreshConfiguration,
    // Helpers
    organizationName: configuration?.name || "Organisation",
    organizationDescription: configuration?.description || "",
    organizationLogo: getOrganizationLogoUrl(configuration),
    primaryColor: configuration?.primaryColor || "#8B5CF6",
    secondaryColor: configuration?.secondaryColor || "#A78BFA",
    isActive: configuration?.isActive ?? true,
  };
};

// Cache global pour éviter les multiples appels à getAllActive()
let allConfigsCache: {
  data: OrgaConfiguration[] | null;
  timestamp: number;
  loading: boolean;
  promise: Promise<OrgaConfiguration[]> | null;
} = {
  data: null,
  timestamp: 0,
  loading: false,
  promise: null,
};

const ALL_CONFIGS_CACHE_TTL = 5 * 60 * 1000; // 5 minutes

/**
 * Hook pour récupérer toutes les configurations d'organisations
 * Utilise un cache global pour éviter les multiples lectures Firestore
 */
export const useAllOrgaConfigurations = () => {
  const [configurations, setConfigurations] = useState<OrgaConfiguration[]>(
    allConfigsCache.data || []
  );
  const [loading, setLoading] = useState(!allConfigsCache.data);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const loadAllConfigurations = async () => {
      const now = Date.now();

      // Vérifier si le cache est encore valide
      if (allConfigsCache.data && (now - allConfigsCache.timestamp) < ALL_CONFIGS_CACHE_TTL) {
        console.log('📦 [CACHE HIT] useAllOrgaConfigurations - utilisation du cache');
        setConfigurations(allConfigsCache.data);
        setLoading(false);
        return;
      }

      // Si un chargement est déjà en cours, attendre son résultat
      if (allConfigsCache.loading && allConfigsCache.promise) {
        console.log('⏳ [CACHE WAIT] useAllOrgaConfigurations - attente du chargement en cours');
        try {
          const configs = await allConfigsCache.promise;
          setConfigurations(configs);
          setLoading(false);
        } catch (err) {
          setError("Erreur lors du chargement des configurations");
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        setError(null);
        allConfigsCache.loading = true;

        console.log(
          "🔍 [CACHE MISS] Chargement de toutes les configurations d'organisations",
        );

        const loadPromise = OrgaConfigurationRepository.getAllActive();
        allConfigsCache.promise = loadPromise;

        const configs = await loadPromise;

        // Mettre à jour le cache
        allConfigsCache.data = configs;
        allConfigsCache.timestamp = Date.now();
        allConfigsCache.loading = false;
        allConfigsCache.promise = null;

        setConfigurations(configs);
      } catch (err) {
        console.error("❌ Erreur lors du chargement des configurations:", err);
        setError("Erreur lors du chargement des configurations");
        allConfigsCache.loading = false;
        allConfigsCache.promise = null;
      } finally {
        setLoading(false);
      }
    };

    loadAllConfigurations();
  }, []);

  return {
    configurations,
    loading,
    error,
    // Fonction pour forcer le rafraîchissement du cache
    refreshCache: () => {
      allConfigsCache.data = null;
      allConfigsCache.timestamp = 0;
    },
  };
};
